import type { StatesConfig } from 'xstate'
import type { TStep } from 'src/types'
import { getComplianceVersion } from 'helpers/getComplianceVersion'
import { getIntellioResumeTarget } from 'helpers/getIntellioResumeTarget'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { PageId } from 'constants/pages'

const isComplianceFlow = (): boolean => getComplianceVersion() !== null

const isUpsellCheckout = (): boolean =>
  useZustandGlobalLocalStore.getState().checkoutContext === 'upsell'

export const getXStateConfigWoofzInt2 = (
  pages: TStep[],
): StatesConfig<any, any, any> => {
  const resumeTarget = getIntellioResumeTarget({
    pages,
    pageIds: {
      certificate: PageId.CERTIFICATE_INTL_2,
      nameEntry: PageId.NAME_ENTRY_INTL_2,
      offer: PageId.OFFER_INTL_2,
      registration: PageId.REGISTRATION_INTL_2,
      report: PageId.REPORT_INTL_2,
    },
  })

  return {
    initial: {
      always: [{ target: resumeTarget }],
    },
    ...pages.reduce(
      (acc, page, index) => ({
        ...acc,
        [page.pageId]: {
          on: {
            NEXT_FLOW_STEP: {
              target: pages[index + 1]?.pageId,
            },
            PREVIOUS_FLOW_STEP: {
              target:
                index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
            },
            SKIP_ONE_STEP: {
              target:
                index + 2 < pages.length
                  ? pages[index + 2].pageId
                  : pages[index].pageId,
            },
            SKIP_ONE_STEP_BACK: {
              target:
                index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
            },
            POST_PAYMENT_CONTINUE: {
              target: PageId.REGISTRATION_INTL_2,
            },
            ...(page.pageId === PageId.EMAIL_INTL_2 && {
              PREVIOUS_FLOW_STEP: [
                {
                  cond: isComplianceFlow,
                  target: PageId.EDUCATION_QUESTION_INTL_2,
                },
                { target: PageId.DISCOVER_FEEDBACKS_INTL_2 },
              ],
            }),
            ...(page.pageId === PageId.OFFER_INTL_2 && {
              NEXT_FLOW_STEP: [
                { cond: isComplianceFlow, target: PageId.CHECKOUT_INTL_2 },
                { target: PageId.PAYMENT_INTL_2 },
              ],
              PREVIOUS_FLOW_STEP: [
                { cond: isComplianceFlow, target: PageId.EMAIL_CONSENT_INTL_2 },
                { target: PageId.EMAIL_INTL_2 },
              ],
            }),
            ...(page.pageId === PageId.CANCEL_OFFER_INTL_2 && {
              NEXT_FLOW_STEP: [
                { cond: isComplianceFlow, target: PageId.CHECKOUT_INTL_2 },
                { target: PageId.PAYMENT_INTL_2 },
              ],
            }),
            ...(page.pageId === PageId.PAYMENT_INTL_2 && {
              NEXT_FLOW_STEP: { target: PageId.UPSELL_INTL_2 },
              PREVIOUS_FLOW_STEP: [
                {
                  cond: (): boolean =>
                    !!useZustandGlobalStore.getState()
                      .hasVisitedCancelOfferIntl2,
                  target: PageId.CANCEL_OFFER_INTL_2,
                },
                { target: PageId.SPECIAL_DISCOUNT_INTL_2 },
              ],
              POST_PAYMENT_CONTINUE: { target: PageId.UPSELL_INTL_2 },
            }),
            ...(page.pageId === PageId.CHECKOUT_INTL_2 && {
              NEXT_FLOW_STEP: [
                { cond: isUpsellCheckout, target: PageId.REGISTRATION_INTL_2 },
                { target: PageId.UPSELL_INTL_2 },
              ],
              PREVIOUS_FLOW_STEP: [
                { cond: isUpsellCheckout, target: PageId.UPSELL_INTL_2 },
                {
                  cond: (): boolean =>
                    !!useZustandGlobalStore.getState()
                      .hasVisitedCancelOfferIntl2,
                  target: PageId.CANCEL_OFFER_INTL_2,
                },
                { target: PageId.SPECIAL_DISCOUNT_INTL_2 },
              ],
              POST_PAYMENT_CONTINUE: [
                { cond: isUpsellCheckout, target: PageId.REGISTRATION_INTL_2 },
                { target: PageId.UPSELL_INTL_2 },
              ],
            }),
            ...(page.pageId === PageId.UPSELL_INTL_2 && {
              NEXT_FLOW_STEP: [
                { cond: isUpsellCheckout, target: PageId.CHECKOUT_INTL_2 },
                { target: PageId.REGISTRATION_INTL_2 },
              ],
            }),
          },
        },
      }),
      {},
    ),
    // [PageId.DOWNLOAD_ESA_INTL_2]: {
    //   type: 'final',
    // },
  }
}
